/**
 * @fileoverview Own the calendar browser's event snapshots, fetched-range coverage, and in-flight request bookkeeping. `kc-internal-frontend/src/components/Calendar/Calendar.js` coordinates fetch/refresh and accepted mutations. Cached rows and range freshness improve navigation without becoming server truth. Persisted browser cache, current query coverage, and provider synchronization are distinct state; a cached range must not be treated as permanent completeness.
 */

import moment from 'moment-timezone';



class EventCacheManager {


  constructor() {
    // Map of event ID to event object for O(1) lookups and deduplication
    this.eventsById = new Map();
    
    // Array of fetched ranges: [{start: moment, end: moment}, ...]
    // These represent periods for which we've already retrieved events from the server
    this.fetchedRanges = [];
    
    // Track in-flight requests to prevent duplicate fetches
    this.pendingFetches = new Set();
    
    // Cache metadata
    this.cacheTimestamp = null;
    
    // Storage key for localStorage persistence
    this.STORAGE_KEY = 'calendar_events_cache';
    this.METADATA_KEY = 'calendar_cache_metadata';
    
    // Load persisted cache on construction
    this.loadFromStorage();
  }



  getAllEvents() {
    return Array.from(this.eventsById.values());
  }



  addEvents(events) {
    events.forEach(event => {
      this.eventsById.set(event.id, event);
    });
    this.cacheTimestamp = new Date().toISOString();
    this.saveToStorage();
  }



  updateEvent(event) {
    if (event && event.id) {
      this.eventsById.set(event.id, event);
      this.cacheTimestamp = new Date().toISOString();
      this.saveToStorage();
    }
  }



  removeEvent(eventId) {
    this.eventsById.delete(eventId);
    this.cacheTimestamp = new Date().toISOString();
    this.saveToStorage();
  }



  markRangeFetched(start, end) {
    this.fetchedRanges.push({
      start: moment(start),
      end: moment(end)
    });
    
    // Optimize: merge overlapping ranges to keep the array manageable
    this._mergeOverlappingRanges();
    
    // Update timestamp and persist
    this.cacheTimestamp = new Date().toISOString();
    this.saveToStorage();
  }



  isRangeFetched(start, end) {
    // Check if the requested range overlaps with any fetched range completely
    return this.fetchedRanges.some(range => {
      return range.start.isSameOrBefore(start) && range.end.isSameOrAfter(end);
    });
  }



  calculateFetchRange(viewType, currentDate, isPrefetch = false) {
    let rangeStart, rangeEnd;
    
    // Define the exact visible range based on view type
    switch (viewType) {
      case 'month-grid':
      case 'month-agenda':
        // For month view, fetch the entire 6-week calendar grid.
        // generateMonthGrid always renders 6 weeks (42 days), so the fetch
        // range must cover all 42 days — not just endOf('month').endOf('week'),
        // which can be shorter when the month doesn't fill all 6 rows.
        rangeStart = moment(currentDate).startOf('month').startOf('week');
        rangeEnd = moment(rangeStart).add(41, 'days').endOf('day');
        break;
        
      case 'week':
        // For week view, fetch the current week
        rangeStart = moment(currentDate).startOf('week');
        rangeEnd = moment(currentDate).endOf('week');
        break;
        
      case 'day':
        // For day view, fetch the current day
        rangeStart = moment(currentDate).startOf('day');
        rangeEnd = moment(currentDate).endOf('day');
        break;
        
      default:
        // Default to month view behavior (6-week grid)
        rangeStart = moment(currentDate).startOf('month').startOf('week');
        rangeEnd = moment(rangeStart).add(41, 'days').endOf('day');
    }

    // Check if we already have this range
    if (this.isRangeFetched(rangeStart, rangeEnd)) {
      return null; // No need to fetch
    }

    // Check if there's already a pending fetch for this range (unless this is a prefetch)
    const rangeKey = `${rangeStart.format('YYYY-MM-DD')}_${rangeEnd.format('YYYY-MM-DD')}`;
    if (!isPrefetch && this.pendingFetches.has(rangeKey)) {
      return null; // Already fetching
    }

    return {
      start: rangeStart,
      end: rangeEnd,
      key: rangeKey
    };
  }



  calculatePrefetchRanges(viewType, currentDate) {
    const ranges = [];
    
    switch (viewType) {
      case 'month-grid':
      case 'month-agenda': {
        // Prefetch previous month
        const prevMonth = moment(currentDate).subtract(1, 'month');
        const prevRange = this.calculateFetchRange(viewType, prevMonth, true);
        if (prevRange) ranges.push(prevRange);
        
        // Prefetch next month
        const nextMonth = moment(currentDate).add(1, 'month');
        const nextRange = this.calculateFetchRange(viewType, nextMonth, true);
        if (nextRange) ranges.push(nextRange);
        break;
      }
      
      case 'week': {
        // Prefetch previous week
        const prevWeek = moment(currentDate).subtract(1, 'week');
        const prevRange = this.calculateFetchRange(viewType, prevWeek, true);
        if (prevRange) ranges.push(prevRange);
        
        // Prefetch next week
        const nextWeek = moment(currentDate).add(1, 'week');
        const nextRange = this.calculateFetchRange(viewType, nextWeek, true);
        if (nextRange) ranges.push(nextRange);
        break;
      }
      
      case 'day': {
        // Prefetch previous day
        const prevDay = moment(currentDate).subtract(1, 'day');
        const prevRange = this.calculateFetchRange(viewType, prevDay, true);
        if (prevRange) ranges.push(prevRange);
        
        // Prefetch next day
        const nextDay = moment(currentDate).add(1, 'day');
        const nextRange = this.calculateFetchRange(viewType, nextDay, true);
        if (nextRange) ranges.push(nextRange);
        break;
      }
    }
    
    return ranges;
  }



  markFetchPending(rangeKey) {
    this.pendingFetches.add(rangeKey);
  }



  markFetchComplete(rangeKey) {
    this.pendingFetches.delete(rangeKey);
  }



  clearCache() {
    this.eventsById.clear();
    this.fetchedRanges = [];
    this.pendingFetches.clear();
    this.cacheTimestamp = null;
    this.clearStorage();
  }



  saveToStorage() {
    try {
      // Convert Map to array for serialization
      const eventsArray = Array.from(this.eventsById.values());
      
      // Save events
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(eventsArray));
      
      // Save metadata including fetched ranges
      const metadata = {
        timestamp: this.cacheTimestamp,
        fetchedRanges: this.fetchedRanges.map(range => ({
          start: range.start.toISOString(),
          end: range.end.toISOString()
        }))
      };
      localStorage.setItem(this.METADATA_KEY, JSON.stringify(metadata));
    } catch (error) {
      // localStorage might be full or disabled - fail silently
      console.warn('Failed to save cache to localStorage:', error);
    }
  }



  loadFromStorage() {
    try {
      // Load events
      const eventsJson = localStorage.getItem(this.STORAGE_KEY);
      if (eventsJson) {
        const eventsArray = JSON.parse(eventsJson);
        eventsArray.forEach(event => {
          this.eventsById.set(event.id, event);
        });
        console.log(`[Cache] Loaded ${eventsArray.length} events from localStorage`);
      }
      
      // Load metadata (timestamp only — fetchedRanges are NOT restored so that
      // a page reload always re-fetches from the API, picking up events created
      // outside the UI such as Slack approvals).
      const metadataJson = localStorage.getItem(this.METADATA_KEY);
      if (metadataJson) {
        const metadata = JSON.parse(metadataJson);
        this.cacheTimestamp = metadata.timestamp;

        // Log cache age
        if (this.cacheTimestamp) {
          const cacheAge = moment().diff(moment(this.cacheTimestamp), 'minutes');
          console.log(`[Cache] Cache age: ${cacheAge} minutes`);
        }
      }
    } catch (error) {
      console.warn('Failed to load cache from localStorage:', error);
      // If loading fails, start with empty cache
      this.eventsById.clear();
      this.fetchedRanges = [];
    }
  }



  clearStorage() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.METADATA_KEY);
    } catch (error) {
      console.warn('Failed to clear cache from localStorage:', error);
    }
  }



  isCacheStale(maxAgeMinutes = 60) {
    if (!this.cacheTimestamp) return true;
    
    const cacheAge = moment().diff(moment(this.cacheTimestamp), 'minutes');
    return cacheAge > maxAgeMinutes;
  }



  _mergeOverlappingRanges() {
    if (this.fetchedRanges.length <= 1) return;

    // Sort ranges by start date
    this.fetchedRanges.sort((a, b) => a.start.valueOf() - b.start.valueOf());

    const merged = [this.fetchedRanges[0]];

    for (let i = 1; i < this.fetchedRanges.length; i++) {
      const current = this.fetchedRanges[i];
      const lastMerged = merged[merged.length - 1];

      // If current range overlaps or is adjacent to last merged range, merge them
      if (current.start.isSameOrBefore(lastMerged.end.clone().add(1, 'day'))) {
        lastMerged.end = moment.max(lastMerged.end, current.end);
      } else {
        merged.push(current);
      }
    }

    this.fetchedRanges = merged;
  }



  getStats() {
    return {
      totalEvents: this.eventsById.size,
      fetchedRanges: this.fetchedRanges.length,
      pendingFetches: this.pendingFetches.size,
      ranges: this.fetchedRanges.map(r => ({
        start: r.start.format('YYYY-MM-DD'),
        end: r.end.format('YYYY-MM-DD')
      }))
    };
  }
}

export default EventCacheManager;
